import React, { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader, Pencil, CheckCircle, Trash2, HelpCircle, Plus, Unplug, Save } from 'lucide-react';
import { formatDateTime } from '../services/utils';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { reportConsole } from '../components/common/utilities/logging-utilities';
import { LoadingSpinner } from '../components/common';
import {
  SharePointConnection,
  DeleteConfirmationModalProps,
  FormValidationErrors,
  State,
  Action,
  fileTypeOptions,
  initialConnection,
  validateField,
  useApiRequest,
  useSharePointApi
} from '../services/api/sharepointConfigService';

// Reducer
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_CONNECTIONS':
      return { ...state, connections: action.payload };
    case 'TOGGLE_ADD_FORM':
      return {
        ...state,
        showAddForm: !state.showAddForm,
        newConnection: state.showAddForm ? initialConnection : state.newConnection,
        isEditing: state.showAddForm ? false : state.isEditing,
        isClientReadOnly: state.showAddForm ? false : state.isClientReadOnly,
        validationErrors: state.showAddForm ? {} : state.validationErrors,
        clientCredentialsChanged: false
      };
    case 'SET_TESTING':
      return { ...state, testing: action.payload };
    case 'SET_SAVING':
      return { ...state, saving: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_VERIFYING_TENANT':
      return { ...state, verifyingTenant: action.payload };
    case 'SET_NEW_CONNECTION':
      return { ...state, newConnection: { ...state.newConnection, ...action.payload } };
    case 'SET_IS_EDITING':
      return { ...state, isEditing: action.payload };
    case 'SET_VALIDATING_CONNECTION_NAME':
      return { ...state, validatingConnectionName: action.payload };
    case 'SET_CLIENT_READ_ONLY':
      return { ...state, isClientReadOnly: action.payload };
    case 'SET_DELETE_MODAL':
      return { ...state, isDeleteModalOpen: action.payload.isOpen, connToDelete: action.payload.connToDelete };
    case 'SET_DELETING_CONNECTION_ID':
      return { ...state, deletingConnectionId: action.payload };
    case 'SET_VALIDATION_ERRORS':
      return { ...state, validationErrors: action.payload };
    case 'SET_CLIENT_CREDENTIALS_CHANGED':
      return { ...state, clientCredentialsChanged: action.payload };
    default:
      return state;
  }
};

// Components
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  connectionName
}) => {
  if (!isOpen) return null;

  reportConsole('Rendering DeleteConfirmationModal', 'info', { connectionName });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center" role="dialog" aria-labelledby="delete-modal-title">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <AlertCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
          <h3 id="delete-modal-title" className="text-lg font-medium text-gray-900">Confirm Delete</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Are you sure you want to delete the SharePoint site <span className="font-medium">{connectionName}</span>?
          This action cannot be undone.
        </p>
        <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {
              reportConsole('Cancel button clicked in DeleteConfirmationModal', 'info', { connectionName });
              onClose();
            }}
            aria-label="Cancel deletion"
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-semibold text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={() => {
              reportConsole('Confirm delete button clicked', 'info', { connectionName });
              onConfirm();
            }}
            aria-label={`Confirm deletion of ${connectionName}`}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const SharePointConfig: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch, executeRequest } = useApiRequest(reducer);
  const { fetchConnections, verifyTenant, saveConnection, deleteConnection } = useSharePointApi();

  const debouncedVerifyTenant = useMemo(() => 
    debounce(async (spTenantId: string) => {
      reportConsole('Starting debounced tenant verification', 'info', { spTenantId });
      if (!spTenantId) {
        dispatch({ type: 'SET_NEW_CONNECTION', payload: { clientId: '', clientSecret: '' } });
        dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: false });
        reportConsole('No tenant ID provided, resetting client credentials', 'info');
        return;
      }

      dispatch({ type: 'SET_VERIFYING_TENANT', payload: true });
      await executeRequest(
        () => verifyTenant(spTenantId),
        (response) => {
          reportConsole('Tenant verification response', 'info', { response });
          if (response.results.length > 0 && response.results[0]?.clientId && response.results[0]?.clientSecret) {
            dispatch({
              type: 'SET_NEW_CONNECTION',
              payload: {
                spTenantId,
                clientId: response.results[0].clientId,
                clientSecret: response.results[0].clientSecret
              }
            });
            dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: true });
            toast.success('Tenant found! Client ID and Secret populated.');
            reportConsole('Tenant found, credentials populated', 'info', { spTenantId });
          } else {
            dispatch({ type: 'SET_NEW_CONNECTION', payload: { clientId: '', clientSecret: '' } });
            dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: false });
            toast.info('No existing credentials found for this tenant.');
            reportConsole('No credentials found for tenant', 'info', { spTenantId });
          }
        },
        (error) => {
          dispatch({ type: 'SET_NEW_CONNECTION', payload: { clientId: '', clientSecret: '' } });
          dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: false });
          toast.error(`Failed to check tenant: ${error.message}`);
          reportConsole('Tenant verification failed', 'error', { error });
        }
      );
      dispatch({ type: 'SET_VERIFYING_TENANT', payload: false });
      reportConsole('Tenant verification completed', 'info', { spTenantId });
    }, 500),
    [executeRequest, verifyTenant, dispatch]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    reportConsole('Input changed', 'info', { name, value });
    dispatch({ type: 'SET_NEW_CONNECTION', payload: { [name]: value } });

    if (name === 'clientId' || name === 'clientSecret') {
      dispatch({ type: 'SET_CLIENT_CREDENTIALS_CHANGED', payload: true });
    }

    const validFields: (keyof Pick<SharePointConnection, 'connectionName' | 'spTenantId' | 'baseUrl'>)[] = [
      'connectionName',
      'spTenantId',
      'baseUrl'
    ];
    if (validFields.includes(name as any)) {
      const error = validateField(name as keyof Pick<SharePointConnection, 'connectionName' | 'spTenantId' | 'baseUrl'>, value);
      dispatch({ type: 'SET_VALIDATION_ERRORS', payload: { ...state.validationErrors, [name]: error } });
    }

    if (name === 'spTenantId' && !state.isEditing) {
      debouncedVerifyTenant(value);
    }
  }, [state.isEditing, state.validationErrors, debouncedVerifyTenant, dispatch]);

  const handleFileTypeChange = useCallback((fileTypeId: string) => {
    reportConsole('File type changed', 'info', { fileTypeId });
    dispatch({
      type: 'SET_NEW_CONNECTION',
      payload: {
        fileInclusions: state.newConnection.fileInclusions.includes(fileTypeId)
          ? state.newConnection.fileInclusions.filter(id => id !== fileTypeId)
          : [...state.newConnection.fileInclusions, fileTypeId]
      }
    });
  }, [state.newConnection.fileInclusions, dispatch]);

  const refreshConnections = useCallback(async (showloader : boolean) => {
    if(showloader) {
    dispatch({ type: 'SET_LOADING', payload: true });
    }
    await executeRequest(
      () => fetchConnections(),
      (data) => {
        dispatch({ type: 'SET_CONNECTIONS', payload: data });
        reportConsole('Connections fetched successfully', 'info', { connections: data });
      },
      (error) => {
        toast.error(`Error fetching connections: ${error.message}`);
        reportConsole('Error fetching connections', 'error', { error });
      }
    );
    if(showloader) {
    dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [executeRequest, fetchConnections, dispatch]);

  useEffect(() => {
    refreshConnections(true);
  }, [refreshConnections]);

  const handleTestConnection = useCallback(async (connection: SharePointConnection, dbUpdateNeeded: boolean) => {
    reportConsole('Testing connection', 'info');
    const errors: FormValidationErrors = {};
    const fields: (keyof Pick<SharePointConnection, 'connectionName' | 'spTenantId' | 'baseUrl'>)[] = ['connectionName', 'spTenantId', 'baseUrl'];
    fields.forEach(field => {
      const error = validateField(field, connection[field] || '');
      if (error) errors[field] = error;
    });

    if (Object.keys(errors).length) {
      dispatch({ type: 'SET_VALIDATION_ERRORS', payload: errors });
      toast.error('Please fix validation errors before testing the connection');
      reportConsole('Validation errors during test', 'warning', { errors });
      return;
    }

    dispatch({ type: 'SET_VALIDATING_CONNECTION_NAME', payload: connection.connectionName });
    dispatch({ type: 'SET_TESTING', payload: true });
    await executeRequest(
      () => saveConnection(connection, 'TestConnection', dbUpdateNeeded),
      () => {
        toast.success('Site connection test successful!');
        reportConsole('Connection test successful', 'info');
      },
      (err) => {
        toast.error('Site connection test failed: ' + (err.message || 'Unknown error'));
        reportConsole('Connection test failed', 'error', { err });
      }
    );
    dispatch({ type: 'SET_TESTING', payload: false });
    dispatch({ type: 'SET_VALIDATING_CONNECTION_NAME', payload: '' });
  }, [executeRequest, saveConnection, dispatch]);

  const handleEditConfig = useCallback(async (connection: SharePointConnection) => {
    reportConsole('Editing configuration', 'info', { connection });
    dispatch({ type: 'SET_IS_EDITING', payload: true });
    dispatch({
      type: 'SET_NEW_CONNECTION',
      payload: {
        connectionId: connection.connectionId,
        connectionName: connection.connectionName || '',
        baseUrl: connection.baseUrl || '',
        spTenantId: connection.spTenantId || '',
        clientId: '',
        clientSecret: '',
        taggedKBCount: connection.taggedKBCount || 0,
        authMethod: connection.authMethod || 'App Registered Oauth',
        fileInclusions: Array.isArray(connection.fileInclusions) ? connection.fileInclusions : [],
        configStatus: connection.configStatus || '',
        lastValidated: connection.lastValidated || '',
        siteName: connection.siteName || '',
      }
    });
    dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: true });
    dispatch({ type: 'TOGGLE_ADD_FORM' });
    toast.info('Editing SharePoint connection: ' + connection.connectionName);

    if (connection.spTenantId) {
      dispatch({ type: 'SET_VERIFYING_TENANT', payload: true });
      await executeRequest(
        () => verifyTenant(connection.spTenantId!),
        (response) => {
          if (response.results.length > 0 && response.results[0]?.clientId && response.results[0]?.clientSecret) {
            dispatch({
              type: 'SET_NEW_CONNECTION',
              payload: {
                clientId: response.results[0].clientId,
                clientSecret: response.results[0].clientSecret
              }
            });
            reportConsole('Credentials auto-populated for edit', 'info', { spTenantId: connection.spTenantId });
          }
        },
        (error) => {
          reportConsole('Failed to fetch credentials for edit', 'error', { error });
        }
      );
      dispatch({ type: 'SET_VERIFYING_TENANT', payload: false });
    }
  }, [dispatch, executeRequest, verifyTenant]);

  const handleSaveConnection = useCallback(async (action: string) => {
    reportConsole('Saving connection', 'info', { newConnection: state.newConnection });
    const errors: FormValidationErrors = {};
    const fields: (keyof Pick<SharePointConnection, 'connectionName' | 'spTenantId' | 'baseUrl'>)[] = ['connectionName', 'spTenantId', 'baseUrl'];
    fields.forEach(field => {
      const error = validateField(field, state.newConnection[field] || '');
      if (error) errors[field] = error;
    });

    if (Object.keys(errors).length) {
      dispatch({ type: 'SET_VALIDATION_ERRORS', payload: errors });
      reportConsole('Validation errors during save', 'warning', { errors });
      return;
    }

    dispatch({ type: 'SET_SAVING', payload: true });
    const { clientId, clientSecret, ...restConnection } = state.newConnection;
    const nestedData = {
      ...(state.isEditing && !state.clientCredentialsChanged ? restConnection : state.newConnection),
      siteName: '/' + new URL(state.newConnection.baseUrl).pathname.substring(1)
    };
    await executeRequest(
      () => saveConnection(nestedData, action),
      async () => {
        await refreshConnections(false);
        dispatch({ type: 'SET_NEW_CONNECTION', payload: initialConnection });
        dispatch({ type: 'TOGGLE_ADD_FORM' });
        dispatch({ type: 'SET_IS_EDITING', payload: false });
        dispatch({ type: 'SET_VALIDATION_ERRORS', payload: {} });
        toast.success(`SharePoint site ${state.isEditing ? 'updated' : 'saved'} successfully!`);
        reportConsole(`Connection ${state.isEditing ? 'updated' : 'saved'} successfully`, 'info');
      },
      (error:any) => {
        toast.error(`Failed to ${state.isEditing ? 'update' : 'save'} configuration: ${error.data.message}`);
        reportConsole(`Failed to ${state.isEditing ? 'update' : 'save'} configuration`, 'error', { error });
      }
    );
    dispatch({ type: 'SET_SAVING', payload: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.newConnection, state.isEditing, state.clientCredentialsChanged, executeRequest, saveConnection, refreshConnections, dispatch]);

  const handleRemoveConnection = useCallback(async (connId: string, connName: string) => {
    reportConsole('Removing connection', 'info', { connId });
    dispatch({ type: 'SET_DELETING_CONNECTION_ID', payload: connId });
    await executeRequest(
      () => deleteConnection(connId, connName),
      async () => {
        await refreshConnections(true);
        toast.success('SharePoint site removed successfully!');
        reportConsole('Connection removed successfully', 'info', { connId });
      },
      (error) => {
        toast.error(`Failed to remove configuration: ${error.message}`);
        reportConsole('Failed to remove configuration', 'error', { error });
      }
    );
    dispatch({ type: 'SET_DELETING_CONNECTION_ID', payload: '' });
  }, [executeRequest, deleteConnection, refreshConnections, dispatch]);

  const openDeleteModal = useCallback((connection: SharePointConnection) => {
    reportConsole('Opening delete modal', 'info', { connection });
    dispatch({
      type: 'SET_DELETE_MODAL',
      payload: { isOpen: true, connToDelete: { name: connection.connectionName, id: connection.connectionId } }
    });
  }, [dispatch]);

  const confirmDelete = useCallback(async () => {
    reportConsole('Confirming delete', 'info', { connToDelete: state.connToDelete });
    if (state.connToDelete) {
      dispatch({ type: 'SET_DELETE_MODAL', payload: { isOpen: false, connToDelete: null } });
      await handleRemoveConnection(state.connToDelete.id, state.connToDelete.name);
    }
  }, [state.connToDelete, handleRemoveConnection, dispatch]);

  const openHelpTopic = (urlParams: string) => {
    navigate(urlParams, { replace: true });
  };

  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">SharePoint Sites</h2>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => dispatch({ type: 'TOGGLE_ADD_FORM' })}
            disabled={state.loading}
            aria-label={state.showAddForm ? 'Cancel adding site' : 'Add new SharePoint site'}
          >
            {state.showAddForm ? 'Cancel' : (
              <>
                <Plus size={16} className="mr-2" />
                Add Site
              </>
            )}
          </button>
        </div>
        {state.loading && (
          <LoadingSpinner
            title="Loading SharePoint Connections"
            message="Please wait while we fetch your connections..."
            size="large"
            fullScreen={false}
            className="py-16"
          />
        )}
        {state.showAddForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">{state.isEditing ? 'Edit SharePoint Site' : 'New SharePoint Site'}</h3>
              <a
                href="/help#faq-7"
                onClick={() => openHelpTopic("/help#faq-6")}
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                aria-label="Help with SharePoint setup"
              >
                <HelpCircle size={16} className="mr-1" />
                Help with SharePoint setup
              </a>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="connectionName" className="block text-sm font-medium text-gray-700">
                  Site Name <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  id="connectionName"
                  type="text"
                  name="connectionName"
                  value={state.newConnection.connectionName}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${state.validationErrors.connectionName ? 'border-red-500' : ''}`}
                  placeholder="e.g., Technical Documentation"
                  maxLength={50}
                  aria-invalid={!!state.validationErrors.connectionName}
                  aria-describedby={state.validationErrors.connectionName ? 'connectionName-error' : undefined}
                />
                {state.validationErrors.connectionName && (
                  <p id="connectionName-error" className="mt-1 text-sm text-red-600">{state.validationErrors.connectionName}</p>
                )}
              </div>
              <div>
                <label htmlFor="spTenantId" className="block text-sm font-medium text-gray-700">
                  SharePoint Tenant ID {state.verifyingTenant && <Loader size={16} className="inline ml-2 animate-spin" aria-label="Verifying tenant" />}
                  <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  id="spTenantId"
                  type="text"
                  name="spTenantId"
                  value={state.newConnection.spTenantId}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${state.isEditing ? 'bg-gray-100' : ''} ${state.validationErrors.spTenantId ? 'border-red-500' : ''}`}
                  placeholder="e.g., 12345678-1234-1234-1234-123456789012"
                  readOnly={state.isEditing}
                  aria-invalid={!!state.validationErrors.spTenantId}
                  aria-describedby={state.validationErrors.spTenantId ? 'spTenantId-error' : undefined}
                />
                {state.validationErrors.spTenantId && (
                  <p id="spTenantId-error" className="mt-1 text-sm text-red-600">{state.validationErrors.spTenantId}</p>
                )}
              </div>
              <div>
                <label htmlFor="baseUrl" className="block text-sm font-medium text-gray-700">
                  Site URL <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  id="baseUrl"
                  type="url"
                  name="baseUrl"
                  value={state.newConnection.baseUrl}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${state.isEditing ? 'bg-gray-100' : ''} ${state.validationErrors.baseUrl ? 'border-red-500' : ''}`}
                  placeholder="https://company.sharepoint.com/sites/example"
                  readOnly={state.isEditing}
                  aria-invalid={!!state.validationErrors.baseUrl}
                  aria-describedby={state.validationErrors.baseUrl ? 'baseUrl-error' : undefined}
                />
                {state.validationErrors.baseUrl && (
                  <p id="baseUrl-error" className="mt-1 text-sm text-red-600">{state.validationErrors.baseUrl}</p>
                )}
              </div>
              <div>
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-2">Include File Types</legend>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {fileTypeOptions.map(fileType => {
                      const isChecked = Array.isArray(state.newConnection.fileInclusions) && state.newConnection.fileInclusions.includes(fileType.id);
                      return (
                        <div key={fileType.id} className="flex items-center">
                          <input
                            id={`filetype-${fileType.id}`}
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleFileTypeChange(fileType.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            aria-labelledby={`filetype-label-${fileType.id}`}
                          />
                          <label id={`filetype-label-${fileType.id}`} htmlFor={`filetype-${fileType.id}`} className="ml-2 block text-sm text-gray-700">
                            {fileType.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">
                        Client ID
                        {state.verifyingTenant && <Loader size={14} className="inline ml-2 animate-spin" aria-label="Fetching credentials" />}
                      </label>
                      {state.isClientReadOnly && (
                        <button
                          type="button"
                          onClick={() => dispatch({ type: 'SET_CLIENT_READ_ONLY', payload: false })}
                          className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                          aria-label="Edit client credentials"
                        >
                          <Pencil size={12} className="mr-1" />
                          Edit
                        </button>
                      )}
                    </div>
                    <input
                      id="clientId"
                      type="text"
                      name="clientId"
                      value={state.newConnection.clientId}
                      onChange={handleInputChange}
                      readOnly={state.isClientReadOnly}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${state.isClientReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      aria-readonly={state.isClientReadOnly}
                    />
                  </div>
                  <div>
                    <label htmlFor="clientSecret" className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                    <input
                      id="clientSecret"
                      type="password"
                      name="clientSecret"
                      value={state.newConnection.clientSecret}
                      onChange={handleInputChange}
                      readOnly={state.isClientReadOnly}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${state.isClientReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      aria-readonly={state.isClientReadOnly}
                    />
                  </div>
                </div>
              <div className="border-t border-gray-200 pt-6">
                {state.validationErrors.content && (
                  <p className="text-sm text-red-600 mb-4">{state.validationErrors.content}</p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                  onClick={() => handleTestConnection(state.newConnection, false)}
                  disabled={state.testing}
                  aria-label="Test connection"
                >
                  {state.testing ? (
                    <>
                      <Loader size={16} className="mr-2 animate-spin" aria-hidden="true" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Unplug size={16} className="mr-2" />
                      Test Connection
                    </>
                  )}
                </button>
                <button
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                  onClick={() => handleSaveConnection(state.isEditing ? 'UpdateConnection' : 'SaveConnection')}
                  disabled={state.saving}
                  aria-label={state.isEditing ? 'Update connection' : 'Save connection'}
                >
                  {state.saving ? (
                    <>
                      <Loader size={16} className="mr-2 animate-spin" aria-hidden="true" />
                      {state.isEditing ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      {state.isEditing ? 'Update Site' : 'Save Site'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {!state.loading && !state.showAddForm && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Site Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Site URL
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Knowledge Bases
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Types
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Validated
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.connections.map((connection) => (
                  <tr key={connection.connectionName}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {connection.connectionName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {connection.baseUrl}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className='ml-4'>{connection.taggedKBCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${connection.configStatus === 'Connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {connection.configStatus || 'Connected'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {connection.fileInclusions.join(', ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      <div className="text-sm">{formatDateTime(connection.lastValidated || 'Just Now') || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-3">
                        <button
                          className="text-green-600 hover:text-green-800 transition-colors"
                          onClick={() => handleEditConfig(connection)}
                          aria-label={`Edit ${connection.connectionName}`}
                          title="Edit site"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          onClick={() => handleTestConnection(connection, true)}
                          aria-label={`Validate ${connection.connectionName}`}
                          title="Test site connection"
                        >
                          {state.validatingConnectionName === connection.connectionName ? (
                            <>
                              <Loader size={16} className="animate-spin" aria-hidden="true" />
                              Testing...
                            </>
                          ) : (
                            <CheckCircle size={16} />
                          )}
                        </button>
                        <button
                          className={`${connection.taggedKBCount > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800 transition-colors'}`}
                          onClick={() => openDeleteModal(connection)}
                          aria-label={`Remove ${connection.connectionName}`}
                          title={connection.taggedKBCount > 0 ? "Cannot delete - Knowledge bases are using this site" : "Delete site"}
                          disabled={connection.taggedKBCount > 0}
                        >
                          {state.deletingConnectionId === connection.connectionId ? (
                            <>
                              <Loader size={16} className="inline mr-1 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {state.connections.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                      No SharePoint sites configured yet. Add your first site to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={state.isDeleteModalOpen}
        onClose={() => dispatch({ type: 'SET_DELETE_MODAL', payload: { isOpen: false, connToDelete: null } })}
        onConfirm={confirmDelete}
        connectionName={state.connToDelete?.name || ''}
      />
    </div>
  );
};

export default SharePointConfig;